import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Toggle from "../../lib/ui/components/toggle/Toggle";
import Checkbox from "../../lib/ui/components/checkbox/Checkbox";
import ToggleField from "../../lib/ui/components/toggle-field/ToggleField";
import Button from "../../lib/ui/components/button/Button";
import Spinner from "../../lib/ui/components/spinner/Spinner";
import Input from "../../lib/ui/components/input/Input";
import SearchInput from "../../lib/ui/components/input/SearchInput";
import Select from "../../lib/ui/components/input/Select";
import DigestFormField from "./DigestFormField";
import { Heading4 } from "../../lib/ui/components/typography/Typography";
import {
  DIGEST_SETTINGS,
  DIGEST_ROLES,
  DIGEST_ROLE,
  DIGEST_ROLES_BULK,
  DIGEST_TEST,
  DIGEST_PREVIEW,
} from "../../lib/urls";
import { isDevEnvironment } from "../../utils/envUtils";

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every two weeks" },
];

const DAYS = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`,
}));

const DIGEST_PREVIEW_SCENARIOS = [
  { value: "", label: "Live data (this job)" },
  {
    value: "with_top_rated",
    label: "Mock — high confidence + top applicants to review",
  },
  { value: "no_top_rated", label: "Mock — no 5s, with 4s" },
  {
    value: "no_fours_or_fives",
    label: "Mock — no 4s or 5s (worth a look)",
  },
  {
    value: "flagged_for_review",
    label: "Mock — bypass / review flags",
  },
];

function formatSchedule(schedule) {
  if (!schedule) return "Default";
  const freq =
    FREQUENCIES.find((f) => f.value === schedule.digest_frequency)?.label ||
    schedule.digest_frequency;
  const day =
    DAYS.find((d) => d.value === schedule.digest_day_of_week)?.label || "";
  const hour =
    HOURS.find((h) => h.value === schedule.digest_hour)?.label || "";
  if (schedule.digest_frequency === "daily") {
    return `${freq}, ${hour}`;
  }
  return `${freq}, ${day} ${hour}`;
}

function roleScheduleLabel(schedule) {
  if (!schedule) return "Default schedule";
  return formatSchedule({
    digest_frequency: schedule.digest_frequency,
    digest_day_of_week: schedule.digest_day_of_week,
    digest_hour: schedule.digest_hour,
  });
}

export const DIGEST_SCHEDULE_FORM_ID = "digest-role-schedule-form";

function ScheduleEditor({
  schedule,
  onSave,
  onReset,
  onCancel,
  inModal = false,
  saving = false,
}) {
  const [frequency, setFrequency] = useState(schedule?.digest_frequency || "weekly");
  const [day, setDay] = useState(schedule?.digest_day_of_week ?? 0);
  const [hour, setHour] = useState(schedule?.digest_hour ?? 9);

  const fields = (
      <div className="flex flex-wrap gap-3">
        <DigestFormField label="Frequency">
          <Select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
          >
            {FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </DigestFormField>
        {frequency !== "daily" && (
          <DigestFormField label="Day">
            <Select
              value={day}
              onChange={(e) => setDay(Number(e.target.value))}
            >
              {DAYS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </Select>
          </DigestFormField>
        )}
        <DigestFormField label="Time">
          <Select
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
          >
            {HOURS.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </Select>
        </DigestFormField>
      </div>
  );

  const submitSchedule = () =>
    onSave({
      digest_frequency: frequency,
      digest_day_of_week: day,
      digest_hour: hour,
    });

  if (inModal) {
    return (
      <form
        id={DIGEST_SCHEDULE_FORM_ID}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!saving) submitSchedule();
        }}
      >
        {fields}
      </form>
    );
  }

  return (
    <div className="mt-2 p-3 border border-gray-200 rounded bg-gray-50 flex flex-col gap-2">
      {fields}
      <div className="flex gap-2">
        <Button size="sm" variant="primary" disabled={saving} onClick={submitSchedule}>
          Save
        </Button>
        {schedule?.is_custom && (
          <Button size="sm" variant="secondary" disabled={saving} onClick={onReset}>
            Reset to default
          </Button>
        )}
        <Button size="sm" variant="tertiary" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Shortlist digest card for User Settings.
 */
export default function ShortlistDigestCard({ token }) {
  const [settings, setSettings] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJobId, setEditingJobId] = useState(null);
  const [testJobId, setTestJobId] = useState("");
  const [previewScenario, setPreviewScenario] = useState("");
  const [saving, setSaving] = useState(false);
  const showPreviewMocks = isDevEnvironment();
  const [selectedRoleIds, setSelectedRoleIds] = useState(() => new Set());
  const [roleSearchQuery, setRoleSearchQuery] = useState("");

  const authHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const [settingsRes, rolesRes] = await Promise.all([
        fetch(DIGEST_SETTINGS, { headers: authHeaders() }),
        fetch(DIGEST_ROLES, { headers: authHeaders() }),
      ]);
      if (!settingsRes.ok || !rolesRes.ok) {
        throw new Error("Failed to load digest settings");
      }
      const settingsData = await settingsRes.json();
      const rolesData = await rolesRes.json();
      setSettings(settingsData);
      setRoles(rolesData.roles || []);
      setTestJobId((prev) => prev || rolesData.roles?.[0]?.job_id || "");
    } catch (e) {
      console.error(e);
      toast.error("Failed to load digest settings");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRoles = useMemo(() => {
    const query = roleSearchQuery.trim().toLowerCase();
    if (!query) return roles;
    return roles.filter((role) =>
      (role.role_name || "").toLowerCase().includes(query)
    );
  }, [roles, roleSearchQuery]);

  const visibleRoleIds = useMemo(
    () => filteredRoles.map((r) => r.job_id),
    [filteredRoles]
  );
  const allRolesSelected =
    visibleRoleIds.length > 0 &&
    visibleRoleIds.every((id) => selectedRoleIds.has(id));

  useEffect(() => {
    if (settings?.company_digest_enabled === false || !settings?.digest_enabled) {
      setSelectedRoleIds(new Set());
    }
  }, [settings?.company_digest_enabled, settings?.digest_enabled]);

  const toggleSelectAll = () => {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (allRolesSelected) {
        visibleRoleIds.forEach((id) => next.delete(id));
      } else {
        visibleRoleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleRoleSelected = (jobId) => {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const saveSettings = async (patch) => {
    const previous = settings;
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    try {
      const res = await fetch(DIGEST_SETTINGS, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          digest_enabled: next.digest_enabled,
          digest_frequency: next.digest_frequency,
          digest_day_of_week: next.digest_day_of_week,
          digest_hour: next.digest_hour,
          digest_timezone: next.digest_timezone,
        }),
      });
      if (!res.ok) throw new Error("save failed");
      if (Object.keys(patch).length > 1 || !("digest_enabled" in patch)) {
        toast.success("Digest settings saved");
      }
    } catch (e) {
      setSettings(previous);
      toast.error("Failed to save digest settings");
      await load({ silent: true });
    } finally {
      setSaving(false);
    }
  };

  const bulkSetSubscribed = async (subscribed) => {
    const jobIds = Array.from(selectedRoleIds);
    if (jobIds.length === 0) return;

    const previousRoles = roles;
    setRoles((prev) =>
      prev.map((role) =>
        selectedRoleIds.has(role.job_id) ? { ...role, subscribed } : role
      )
    );
    setSaving(true);
    try {
      const res = await fetch(DIGEST_ROLES_BULK, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ job_ids: jobIds, subscribed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : "Failed to update selected roles"
        );
      }
      toast.success(
        subscribed
          ? `Digest turned on for ${data.updated} role${data.updated === 1 ? "" : "s"}`
          : `Digest turned off for ${data.updated} role${data.updated === 1 ? "" : "s"}`
      );
      setSelectedRoleIds(new Set());
      await load({ silent: true });
    } catch (e) {
      setRoles(previousRoles);
      toast.error(e.message || "Failed to update selected roles");
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async (jobId, body) => {
    const previousRoles = roles;
    setRoles((prev) =>
      prev.map((role) => {
        if (role.job_id !== jobId) return role;
        if (body.subscribed !== undefined) {
          return { ...role, subscribed: body.subscribed };
        }
        return role;
      })
    );
    setSaving(true);
    try {
      const res = await fetch(DIGEST_ROLE(jobId), {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("save failed");
      toast.success("Role digest updated");
      setEditingJobId(null);
      await load({ silent: true });
    } catch (e) {
      setRoles(previousRoles);
      toast.error("Failed to update role digest");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testJobId) {
      toast.error("Pick a role to test");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(DIGEST_TEST, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ job_id: testJobId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        const message =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg || d).join(", ")
              : "Failed to send test digest";
        throw new Error(message);
      }
      toast.success("Test digest sent — check your inbox (and spam)");
    } catch (e) {
      toast.error(e.message || "Failed to send test digest");
    } finally {
      setSaving(false);
    }
  };

  const previewDigest = async () => {
    if (!testJobId) {
      toast.error("Pick a role to preview");
      return;
    }
    const previewTab = window.open("", "_blank");
    if (!previewTab) {
      toast.error("Allow pop-ups to open the preview tab");
      return;
    }
    try {
      previewTab.document.open();
      previewTab.document.write(
        "<!DOCTYPE html><html><head><title>Digest preview</title></head>" +
          "<body style='font-family:Arial,sans-serif;padding:24px;color:#4b5563;'>" +
          "Loading preview…</body></html>"
      );
      previewTab.document.close();
    } catch {
      previewTab.close();
      toast.error("Could not open preview tab");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        DIGEST_PREVIEW(testJobId, previewScenario || undefined),
        { headers: authHeaders() }
      );
      if (!res.ok) {
        previewTab.close();
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        throw new Error(
          typeof detail === "string" ? detail : "Failed to load digest preview"
        );
      }
      const html = await res.text();
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const blobUrl = URL.createObjectURL(blob);
      previewTab.location.href = blobUrl;
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      previewTab.close();
      toast.error(e.message || "Failed to load digest preview");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="p-4 border border-gray-200 rounded">
        {loading ? (
          <p className="text-body text-gray-500">Loading digest settings…</p>
        ) : (
          <p className="text-body text-gray-500">Could not load digest settings.</p>
        )}
      </div>
    );
  }

  const browserTz =
    settings.digest_timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "America/New_York";

  const companyDisabled = settings.company_digest_enabled === false;
  const masterSwitchDisabled = saving || companyDisabled;
  // When company digests are off, show the personal switch as off even if
  // the stored preference is still true — nothing will send either way.
  const receiveDigestsChecked = companyDisabled
    ? false
    : !!settings.digest_enabled;
  const settingsDisabled = companyDisabled || !settings.digest_enabled;
  const settingsInactive = companyDisabled || !settings.digest_enabled;

  return (
    <div>
      <Heading4 className="mb-2">Shortlist Digest</Heading4>
      <p className="text-body text-gray-600 mb-4">
        Get a review email for each role on your schedule. Defaults come from the
        ATS hiring team when available.
      </p>

      {companyDisabled && (
        <p className="text-body text-amber-700 mb-3">
          Digests are turned off for your company by an admin.
        </p>
      )}

      <ToggleField
        className="py-3 border-b border-gray-100"
        label="Receive digests"
        description="When on, you get scheduled shortlist emails for the roles you enable below."
        checked={receiveDigestsChecked}
        disabled={masterSwitchDisabled}
        disabledTooltip={
          companyDisabled
            ? "Contact your company admin if you want shortlist digests turned on."
            : undefined
        }
        onChange={(v) => saveSettings({ digest_enabled: v })}
      />

      <div className="relative mt-4" aria-busy={saving}>
        {saving && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded bg-white/60"
            aria-hidden
          >
            <Spinner />
          </div>
        )}

      <div
        className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${
          settingsInactive ? "opacity-60" : ""
        }`}
      >
        <DigestFormField label="Default frequency">
          <Select
            value={settings.digest_frequency}
            disabled={settingsDisabled}
            onChange={(e) => saveSettings({ digest_frequency: e.target.value })}
          >
            {FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </DigestFormField>
        {settings.digest_frequency !== "daily" && (
          <DigestFormField label="Default day">
            <Select
              value={settings.digest_day_of_week}
              disabled={settingsDisabled}
              onChange={(e) =>
                saveSettings({ digest_day_of_week: Number(e.target.value) })
              }
            >
              {DAYS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </Select>
          </DigestFormField>
        )}
        <DigestFormField label="Default time">
          <Select
            value={settings.digest_hour}
            disabled={settingsDisabled}
            onChange={(e) => saveSettings({ digest_hour: Number(e.target.value) })}
          >
            {HOURS.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </Select>
        </DigestFormField>
      </div>

      <DigestFormField
        label="Timezone"
        className={`mt-3 max-w-md ${settingsInactive ? "opacity-60" : ""}`}
      >
        <Input
          value={browserTz}
          disabled={settingsDisabled}
          onChange={(e) => saveSettings({ digest_timezone: e.target.value })}
        />
      </DigestFormField>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-2">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <h3 className="text-body font-semibold">Roles</h3>
          {selectedRoleIds.size > 0 && !settingsDisabled && (
            <>
              <span className="text-body text-gray-600">
                {selectedRoleIds.size} selected
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={saving}
                onClick={() => bulkSetSubscribed(false)}
              >
                Turn off
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={saving}
                onClick={() => bulkSetSubscribed(true)}
              >
                Turn on
              </Button>
            </>
          )}
        </div>
        {roles.length > 0 && (
          <div
            className={`flex flex-wrap items-center justify-end gap-3 ml-auto shrink-0 ${
              settingsInactive ? "opacity-60" : ""
            }`}
          >
            {roleSearchQuery.trim() && (
              <span className="text-body text-gray-600 whitespace-nowrap">
                Showing {filteredRoles.length} of {roles.length}{" "}
                {roles.length === 1 ? "role" : "roles"}
              </span>
            )}
            <SearchInput
              value={roleSearchQuery}
              onChange={(e) => setRoleSearchQuery(e.target.value)}
              onClear={() => setRoleSearchQuery("")}
              placeholder="Search roles"
              className="w-72 sm:w-80"
              disabled={settingsDisabled}
              aria-label="Search roles"
            />
          </div>
        )}
      </div>
      <div
        className={`border border-gray-200 rounded overflow-hidden ${
          settingsInactive ? "opacity-60" : ""
        }`}
      >
        <table className="w-full text-body">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 w-10">
                <Checkbox
                  checked={allRolesSelected}
                  disabled={
                    settingsDisabled ||
                    filteredRoles.length === 0 ||
                    saving
                  }
                  onChange={toggleSelectAll}
                  aria-label="Select all visible roles"
                />
              </th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">On</th>
              <th className="px-3 py-2 font-medium">Schedule</th>
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-gray-500">
                  No open roles yet.
                </td>
              </tr>
            )}
            {roles.length > 0 && filteredRoles.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-gray-500">
                  No roles match your search.
                </td>
              </tr>
            )}
            {filteredRoles.map((role) => (
              <tr key={role.job_id} className="border-t border-gray-100 align-top">
                <td className="px-3 py-3 align-middle">
                  <Checkbox
                    checked={selectedRoleIds.has(role.job_id)}
                    disabled={settingsDisabled || saving}
                    onChange={() => toggleRoleSelected(role.job_id)}
                    aria-label={`Select ${role.role_name}`}
                  />
                </td>
                <td className="px-3 py-3">
                  <div className="font-medium">{role.role_name}</div>
                  {role.hiring_team_matched && (
                    <div className="text-gray-500 mt-0.5">
                      Hiring team
                      {role.ats_display_name ? ` in ${role.ats_display_name}` : ""}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <Toggle
                    checked={companyDisabled ? false : !!role.subscribed}
                    disabled={settingsDisabled}
                    onChange={(v) => updateRole(role.job_id, { subscribed: v })}
                  />
                </td>
                <td className="px-3 py-3">
                  {editingJobId === role.job_id && !settingsDisabled ? (
                    <ScheduleEditor
                      schedule={role.schedule}
                      onCancel={() => setEditingJobId(null)}
                      onReset={() =>
                        updateRole(role.job_id, { reset_schedule: true })
                      }
                      onSave={(patch) => updateRole(role.job_id, patch)}
                    />
                  ) : (
                    <button
                      type="button"
                      className={`text-left underline ${
                        settingsDisabled
                          ? "text-gray-400 cursor-not-allowed no-underline"
                          : "text-primary-main"
                      }`}
                      disabled={settingsDisabled}
                      onClick={() => setEditingJobId(role.job_id)}
                    >
                      {role.schedule?.is_custom
                        ? formatSchedule(role.schedule)
                        : `Default (${formatSchedule({
                            digest_frequency: settings.digest_frequency,
                            digest_day_of_week: settings.digest_day_of_week,
                            digest_hour: settings.digest_hour,
                          })})`}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className={`flex flex-wrap items-end gap-3 mt-4 ${
          settingsInactive ? "opacity-60" : ""
        }`}
      >
        <DigestFormField label="Send me a test" className="min-w-[12rem]">
          <Select
            value={testJobId}
            disabled={settingsDisabled}
            onChange={(e) => setTestJobId(e.target.value)}
          >
            {roles.map((r) => (
              <option key={r.job_id} value={r.job_id}>
                {r.role_name}
              </option>
            ))}
          </Select>
        </DigestFormField>
        {showPreviewMocks && (
          <DigestFormField label="Preview variant" className="min-w-[14rem]">
            <Select
              value={previewScenario}
              disabled={settingsDisabled}
              onChange={(e) => setPreviewScenario(e.target.value)}
            >
              {DIGEST_PREVIEW_SCENARIOS.map((s) => (
                <option key={s.value || "live"} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </DigestFormField>
        )}
        <Button
          size="sm"
          variant="secondary"
          disabled={saving || !testJobId}
          onClick={previewDigest}
        >
          Preview in browser
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={settingsDisabled || !testJobId || saving}
          onClick={sendTest}
        >
          Send test email
        </Button>
      </div>
      </div>
    </div>
  );
}

export {
  formatSchedule,
  roleScheduleLabel,
  FREQUENCIES,
  DAYS,
  HOURS,
  ScheduleEditor,
};
